import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogOut, User, Building, Mail, Phone, Globe, Award, Settings, CreditCard, CheckCircle, Clock, AlertCircle, TrendingUp, DollarSign, Home, Heart, Bell, ShoppingCart, ChevronDown, Star, Calendar, MapPin, Hotel, BedDouble, Package, XCircle, RefreshCw, MessageCircle, Leaf, Zap, Search, Download, Sparkles, Plane, ArrowRight } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import bookingService from '../services/bookingService'
import experienceService from '../services/experienceService'
import hotelService from '../services/hotelService'
import * as offMarketStaysService from '../services/offMarketStaysService'
import LoadingSpinner from '../components/LoadingSpinner'
import clientService from '../services/clientService'
import jsPDF from 'jspdf';
// Import autotable properly
import autoTable from 'jspdf-autotable';
import MainLayout from '../components/Layout/MainLayout';
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'

function renderDashboardStaySourceLine(booking) {
  const stay = getHotelStaySourcePresentation(booking)
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1">
      <span
        className={`inline-flex px-2 py-0.5 text-[10px] font-poppinsRegular uppercase tracking-wide border ${
          stay.variant === 'offMarket'
            ? 'bg-amber-50 text-amber-900 border-amber-200'
            : 'bg-white text-gray-700 border-gray-200/60'
        }`}
      >
        {stay.variant === 'offMarket' ? 'Off-market' : 'GDS hotel'}
      </span>
      {stay.detailText ? (
        <span className="text-xs text-gray-500 font-poppinsLight">{stay.detailText}</span>
      ) : null}
    </div>
  )
}

const DASHBOARD_HERO_BG =
  'https://s3.eu-north-1.amazonaws.com/spaceaxonall.com/origin+mvp+assets/blog/img5.jpg'

const DARK = '#111111'
const CHARCOAL = '#3a3a3a'
const LIGHT_BG = '#f7f7f7'

const FALLBACK_IMAGES = {
  experiences: 'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-lina-12238284.jpg',
  hotels: 'https://s3.eu-north-1.amazonaws.com/spaceaxonall.com/origin+mvp+assets/landing_page/axonall-coromandel.jpg',
  jets: 'https://cdn.pixabay.com/photo/2017/09/27/16/56/airplane-2794957_1280.jpg',
  offMarket: 'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg',
}

function getExperienceCoverImage(experience) {
  if (experience?.images?.length) {
    const cover = experience.images.find((img) => img.isCover) || experience.images[0]
    return cover.url || cover
  }
  return FALLBACK_IMAGES.experiences
}

function getHotelImageUrl(hotel) {
  return (
    hotel?.HotelImageInfo?.ImageItem?.Image?.Url ||
    hotel?.HotelImageInfo?.ImageItem?.[0]?.Image?.Url ||
    hotel?.HotelImageInfo?.[0]?.Image?.Url ||
    hotel?.Image?.Url ||
    null
  )
}

function getHotelDisplayName(hotel) {
  return hotel?.HotelInfo?.HotelName || hotel?.hotelName || hotel?.name || 'Luxury Hotel'
}

function getOffMarketCoverImage(property) {
  const imgs = property?.adminImages
  if (Array.isArray(imgs) && imgs.length > 0) {
    const prioritised = [...imgs].sort((a, b) => {
      if (a.categoryCode === 1) return -1
      if (b.categoryCode === 1) return 1
      return 0
    })
    return prioritised[0]?.s3Url || prioritised[0]?.sabreImageUrl || FALLBACK_IMAGES.offMarket
  }
  return FALLBACK_IMAGES.offMarket
}

function formatIsoLocalDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildOffMarketHotelUrl(property) {
  const code = property?.hotelCode
  if (code == null || String(code).trim() === '') return '/off-market-stays'
  const checkIn = new Date()
  checkIn.setDate(checkIn.getDate() + 1)
  const checkOut = new Date(checkIn)
  checkOut.setDate(checkOut.getDate() + 6)
  const params = new URLSearchParams({
    checkInDate: formatIsoLocalDate(checkIn),
    checkOutDate: formatIsoLocalDate(checkOut),
    adults: '1',
    children: '0',
    currency: 'USD',
    offMarketStay: '1',
  })
  if (property._id != null) params.set('offMarketPropertyId', String(property._id))
  return `/hotel/${encodeURIComponent(String(code).trim())}?${params.toString()}`
}

function DashboardSectionHeader({ eyebrow, title, rightSlot = null }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6 sm:mb-8">
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight mb-2" style={{ color: CHARCOAL }}>
          {eyebrow}
        </p>
        <h3 className="text-xl sm:text-2xl md:text-[2rem] font-AXONALLPrimeLight tracking-wider uppercase leading-tight" style={{ color: DARK }}>
          {title}
        </h3>
        <div className="w-8 h-px mt-3" style={{ backgroundColor: CHARCOAL }} />
      </div>
      {rightSlot}
    </div>
  )
}

function PerformanceStatCard({ icon: Icon, label, title, value, subtitle, footnote }) {
  return (
    <div className="group bg-white border border-gray-200/60 p-5 sm:p-6 hover:border-gray-900 transition-colors duration-300">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: DARK }}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <span className="text-[10px] tracking-[0.28em] uppercase font-poppinsLight text-gray-400 pt-1">{label}</span>
      </div>
      <p className="text-[11px] font-poppinsRegular tracking-[0.18em] uppercase mb-2" style={{ color: DARK }}>{title}</p>
      <p className="text-3xl sm:text-4xl font-AXONALLPrimeRegular tracking-tight" style={{ color: DARK }}>{value}</p>
      {footnote ? (
        <p className="text-xs font-poppinsLight text-gray-400 mt-2">{footnote}</p>
      ) : subtitle ? (
        <p className="text-sm font-poppinsLight text-gray-500 mt-2">{subtitle}</p>
      ) : null}
    </div>
  )
}

function StudioInventorySidebar({ slides, isLoading, hideHeader = false }) {
  if (isLoading) {
    return (
      <aside className="animate-pulse">
        <div className="bg-white border border-gray-200/60 p-4">
          <div className="h-3 bg-gray-100 w-24 mb-4" />
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div className="aspect-[3/2] bg-gray-100 mb-2" />
                <div className="h-2.5 bg-gray-100 w-3/4 mb-1.5" />
                <div className="h-2 bg-gray-100 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </aside>
    )
  }

  if (!slides.length) return null

  return (
    <aside className="lg:sticky lg:top-28 lg:self-start">
      <div className="bg-white border border-gray-200/60">
        {!hideHeader && (
          <div className="px-4 pt-4 pb-3 border-b border-gray-100">
            <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight" style={{ color: CHARCOAL }}>
              From the Studio
            </p>
            <p className="font-poppinsLight text-xs text-gray-400 mt-1 leading-relaxed">
              Curated inventory for Reserve advisors
            </p>
          </div>
        )}

        <div className="p-3 pt-20 flex flex-col gap-3">
          {slides.map((item) => {
            const TileIcon = item.icon
            return (
              <button
                key={item.id}
                type="button"
                onClick={item.onExplore}
                className="text-left group flex flex-col border border-gray-100 hover:border-gray-300 transition-colors duration-200 overflow-hidden"
              >
                {/* Image */}
                <div className="overflow-hidden bg-gray-100 w-full">
                  <img
                    src={item.image}
                    alt=""
                    className="w-full aspect-[3/2] object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                </div>

                {/* Content */}
                <div className="p-2.5 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TileIcon className={`h-3 w-3 shrink-0 ${item.iconAccent || ''}`} style={{ color: item.iconAccent ? undefined : CHARCOAL }} aria-hidden />
                    <p className="text-[9px] tracking-[0.22em] uppercase font-poppinsLight truncate" style={{ color: CHARCOAL }}>
                      {item.eyebrow}
                    </p>
                    {item.badge}
                  </div>
                  <h4 className="font-poppinsRegular text-[10px] tracking-[0.1em] uppercase leading-snug line-clamp-2 mb-1.5" style={{ color: DARK }}>
                    {item.title}
                  </h4>
                  <p className="font-poppinsLight text-[10px] text-gray-400 leading-relaxed line-clamp-2 mb-2.5">
                    {item.subtitle}
                  </p>
                  <span
                    className="mt-auto inline-flex items-center justify-center gap-1.5 w-full text-[9px] tracking-[0.18em] uppercase font-poppinsRegular text-white py-2 group-hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: DARK }}
                  >
                    {item.exploreLabel}
                    <ArrowRight className="h-2.5 w-2.5" />
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </aside>
  )
}

// Add autoTable to jsPDF prototype
jsPDF.autoTable = autoTable;

const DashboardPage = () => {
  const [profile, setProfile] = useState(null)
  const [dashboardStats, setDashboardStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const navigate = useNavigate()
  const [mounted, setMounted] = useState(false)
  const location = useLocation()

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [environmentalReport, setEnvironmentalReport] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [featuredExperiences, setFeaturedExperiences] = useState([])
  const [featuredHotels, setFeaturedHotels] = useState([])
  const [featuredOffMarket, setFeaturedOffMarket] = useState([])
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(false)
  const [studioDrawerOpen, setStudioDrawerOpen] = useState(false)

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true)
      try {
        await Promise.all([
          loadProfile(),
          loadDashboardStats(),
          loadClients()
        ])
      } catch (error) {
        console.error('Failed to load initial data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialData()
  }, [])


  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!profile) return

    let cancelled = false

    const loadFeaturedContent = async () => {
      setIsLoadingFeatured(true)
      try {
        const [expResult, offMarketResult, destinationsResult] = await Promise.allSettled([
          experienceService.getFeaturedExperiences(2),
          offMarketStaysService.getAllOffMarketStays({ page: 1, limit: 1 }),
          hotelService.getPopularDestinationsList(),
        ])

        if (cancelled) return

        if (expResult.status === 'fulfilled' && expResult.value?.success && Array.isArray(expResult.value.data)) {
          setFeaturedExperiences(expResult.value.data.slice(0, 2))
        }

        if (offMarketResult.status === 'fulfilled' && offMarketResult.value?.success && Array.isArray(offMarketResult.value.data)) {
          setFeaturedOffMarket(offMarketResult.value.data.slice(0, 1))
        }

        if (destinationsResult.status === 'fulfilled') {
          const destinations = destinationsResult.value?.destinations || []
          if (destinations.length > 0) {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            const nextWeek = new Date()
            nextWeek.setDate(nextWeek.getDate() + 7)

            try {
              const hotelResponse = await hotelService.getPopularDestinationHotels(destinations[0], {
                checkInDate: tomorrow.toISOString().split('T')[0],
                checkOutDate: nextWeek.toISOString().split('T')[0],
                adults: 1,
                children: 0,
                currency: 'USD',
                radius: 200,
                limit: 1,
              })
              if (!cancelled) {
                setFeaturedHotels((hotelResponse?.hotels || []).slice(0, 1))
              }
            } catch (error) {
              console.error('Failed to load featured hotels:', error)
            }
          }
        }
      } finally {
        if (!cancelled) setIsLoadingFeatured(false)
      }
    }

    loadFeaturedContent()
    return () => { cancelled = true }
  }, [profile])

  const studioCarouselSlides = useMemo(() => {
    const offMarket = featuredOffMarket[0]

    const offMarketSubtitle = offMarket?.location?.city && offMarket?.location?.countryName
      ? `${offMarket.location.city} · ${offMarket.location.countryName}`
      : 'Exclusive off-market properties reserved for approved Reserve advisors.'

    const offMarketSlide = {
      id: 'off-market',
      eyebrow: 'Off-Market Stays',
      title: offMarket?.name || 'Off-Market Stays',
      subtitle: offMarket ? offMarketSubtitle : 'Exclusive off-market properties reserved for approved Reserve advisors.',
      image: offMarket ? getOffMarketCoverImage(offMarket) : FALLBACK_IMAGES.offMarket,
      icon: BedDouble,
      iconAccent: 'text-amber-300',
      badge: !profile?.hasOffMarketAccess ? (
        <span className="bg-amber-400/95 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-neutral-900">
          Premium
        </span>
      ) : null,
      exploreLabel: offMarket ? 'View stay' : 'Browse inventory',
      browsePath: '/off-market-stays',
      onExplore: () => offMarket ? navigate(buildOffMarketHotelUrl(offMarket)) : navigate('/off-market-stays'),
    }

    const experienceSlides = featuredExperiences.slice(0, 3).map((exp) => {
      const location = [exp?.location?.city, exp?.location?.country].filter(Boolean).join(' · ')
      return {
        id: `experience-${exp._id || exp.id}`,
        eyebrow: 'Experience',
        title: exp.name || exp.title || 'Curated Experience',
        subtitle: location || exp.shortDescription || exp.description?.slice(0, 80) || 'A curated Axonall experience.',
        image: getExperienceCoverImage(exp),
        icon: Sparkles,
        iconAccent: 'text-violet-400',
        badge: null,
        exploreLabel: 'View experience',
        browsePath: '/experiences',
        onExplore: () => navigate(`/experience/${exp._id || exp.id}`),
      }
    })

    return experienceSlides
  }, [featuredOffMarket, featuredExperiences, profile?.hasOffMarketAccess, navigate])

  const loadProfile = async () => {
    try {
      const response = await travelAgentService.getProfile()
      const p = response.data
      setProfile(p)
      if (p && typeof p.hasOffMarketAccess === 'boolean') {
        travelAgentService.patchCachedUser({
          hasOffMarketAccess: p.hasOffMarketAccess,
          ...(p.offMarketAccessGrantedAt != null ? { offMarketAccessGrantedAt: p.offMarketAccessGrantedAt } : {}),
        })
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadDashboardStats = async () => {
    try {
      const response = await bookingService.getDashboardStats()
      setDashboardStats(response.data)
    } catch (error) {
      console.error('Failed to load dashboard stats:', error)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const handleLogout = () => {
    travelAgentService.logout()
    navigate('/login')
  }

  const generateClientEnvironmentalPDF = (clientData, environmentalData) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 15
    const pageHeight = doc.internal.pageSize.getHeight()

    // Add header
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('Environmental Impact Report', 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.text('AXONALL Travel Advisor Portal', 105, 30, { align: 'center' });

    // Client Information
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text('Client Information', 14, 55);

    doc.setFontSize(10);

    // Safe access to client data
    const client = clientData || {};
    const fullName = client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'N/A';
    const email = client.email || 'N/A';
    const phone = client.phone || 'N/A';
    const customerId = client.customerId || 'N/A';

    doc.text(`Name: ${fullName}`, 14, 65);
    doc.text(`Email: ${email}`, 14, 72);
    doc.text(`Phone: ${phone}`, 14, 79);
    doc.text(`Customer ID: ${customerId}`, 14, 86);

    // Environmental Impact Summary
    doc.setFontSize(16);
    doc.text('Environmental Impact Summary', 14, 100);

    doc.setFontSize(10);

    // Safe access to environmental data
    const envData = environmentalData || {};
    const dateRange = envData.dateRange || {};
    const startDate = dateRange.startDate || 'N/A';
    const endDate = dateRange.endDate || 'N/A';
    const bookingCount = envData.bookingCount || 0;
    const totalCarbonEmission = envData.totalCarbonEmission || 0;
    const totalElectricityValue = envData.totalElectricityValue || 0;

    doc.text(`Date Range: ${startDate} to ${endDate}`, 14, 110);
    doc.text(`Total Bookings: ${bookingCount}`, 14, 117);
    doc.text(`Total Carbon Emission: ${totalCarbonEmission} kg CO2`, 14, 124);
    doc.text(`Total Electricity Used: ${totalElectricityValue} kWh`, 14, 131);

    // Bookings Table
    doc.setFontSize(16);
    doc.text('Booking Details', 14, 145);

    // Safe access to bookings array
    const bookings = envData.bookings || [];
    const tableData = bookings.map(booking => [
      booking.confirmationId || 'N/A',
      booking.hotelName || 'Unknown Hotel',
      booking.endDate ? new Date(booking.endDate).toLocaleDateString() : 'N/A',
      (booking.environmentalImpact?.carbonEmission || 0) + ' kg',
      (booking.environmentalImpact?.electricityValue || 0) + ' kWh',
      booking.environmentalImpact?.tier || 'No data'
    ]);

    if (tableData.length > 0) {
      // Use the autoTable function directly
      autoTable(doc, {
        startY: 150,
        head: [['Booking ID', 'Hotel', 'End Date', 'Carbon Emission', 'Electricity', 'Tier']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [15, 23, 42]
        }
      });
    } else {
      doc.setFontSize(12);
      doc.text('No booking data available for the selected criteria.', 14, 150);
    }

    // Footer
    const totalPages = doc.internal.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)

      // Page number
      doc.setFontSize(8)
      doc.setTextColor(100, 116, 139)
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' })

      // Footer background
      doc.setFillColor(15, 23, 42)
      doc.rect(0, pageHeight - 15, pageWidth, 15, 'F')

      // Footer text
      doc.setFontSize(7)
      doc.setTextColor(203, 213, 225)
      doc.setFont('helvetica', 'normal')
      doc.text('Axonall Luxury Travel • 24/7 Support: support@axonall.com • +1-800-AXONALL',
        pageWidth / 2, pageHeight - 9, { align: 'center' })
      doc.text('Official Booking Voucher • Unauthorized duplication prohibited • www.axonall.com',
        pageWidth / 2, pageHeight - 4, { align: 'center' })
    }

    // Security watermark on all pages
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(35)
      doc.setTextColor(241, 245, 249)
      doc.setFont('helvetica', 'bold')
      doc.setGState(new doc.GState({ opacity: 0.03 }))
      doc.text('AXONALL', pageWidth / 2, pageHeight / 2, {
        align: 'center',
        angle: 45
      })
    }

    return doc;
  };


  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientService.getAgentClients();
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleSearchClient = async () => {
    if (!selectedClient || !startDate || !endDate) {
      return;
    }

    setIsLoadingReport(true);
    try {
      const response = await bookingService.getClientEnvironmentalImpact(
        selectedClient,
        startDate,
        endDate
      );


      setEnvironmentalReport(response.data);
    } catch (error) {
      console.error('Failed to load environmental report:', error);
    } finally {
      setIsLoadingReport(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!environmentalReport) {
      return;
    }

    // Comprehensive validation
    if (!environmentalReport.client && !environmentalReport.environmentalImpact) {
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const doc = generateClientEnvironmentalPDF(
        environmentalReport.client,
        environmentalReport.environmentalImpact
      );

      const clientId = environmentalReport.client?.customerId || 'unknown';
      const fileName = `environmental-report-${clientId}-${startDate}-to-${endDate}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };


  const filteredClients = clients.filter(client =>
    client.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.customerId?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: LIGHT_BG }}>
          <div className="text-center">
            <LoadingSpinner size="xl" />
            <p className="mt-4 text-gray-500 font-poppinsLight text-sm tracking-wide">Loading your dashboard...</p>
          </div>
        </div>
      </MainLayout>
    )
  }
  return (
    <MainLayout>
      <div className="min-h-screen" style={{ backgroundColor: LIGHT_BG }}>


        {/* Hero */}
        <section className="relative flex flex-col min-h-[20rem] sm:min-h-[28rem] lg:min-h-[31rem] pb-10 sm:pb-14 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("${DASHBOARD_HERO_BG}")`
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.82) 100%)'
            }}
            aria-hidden="true"
          />
          <div className="relative z-10 flex flex-1 flex-col justify-start pt-14 sm:pt-16 md:pt-20 lg:pt-24 pb-8 sm:pb-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <p className="text-[10px] tracking-[0.5em] uppercase text-white/55 mb-5 font-poppinsLight">
                Welcome back
              </p>
              <div className="w-8 h-px bg-white/30 mb-7" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-AXONALLPrimeRegular tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-4 break-words leading-[1.1]">
                {profile?.name ? profile.name : 'Travel Advisor'}
              </h1>
              <p className="text-sm text-white/70 max-w-2xl mb-10 font-poppinsLight leading-relaxed tracking-wide">
                Member of Axonall Reserve
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/experiences')}
                  className="inline-flex items-center justify-center w-full sm:w-auto border border-white/60 text-white hover:bg-white hover:text-gray-900 text-[11px] tracking-[0.28em] uppercase font-poppinsRegular py-3.5 px-8 transition-all duration-300"
                >
                  Browse Experiences
                </button>
                <button
                  onClick={() => navigate('/hotels')}
                  className="inline-flex items-center justify-center w-full sm:w-auto border border-white/60 text-white hover:bg-white hover:text-gray-900 text-[11px] tracking-[0.28em] uppercase font-poppinsRegular py-3.5 px-8 transition-all duration-300"
                >
                  Browse Luxury Hotels
                </button>
                <button
                  onClick={() => navigate('/private-jets')}
                  className="inline-flex items-center justify-center w-full sm:w-auto border border-white/60 text-white hover:bg-white hover:text-gray-900 text-[11px] tracking-[0.28em] uppercase font-poppinsRegular py-3.5 px-8 transition-all duration-300"
                >
                  Browse Private Jets
                </button>
              </div>
            </div>
          </div>
        </section>


        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="text-center mb-10 sm:mb-12">
              <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight mb-3" style={{ color: CHARCOAL }}>
                Account Overview
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-AXONALLPrimeLight tracking-wider uppercase mb-3 px-1" style={{ color: DARK }}>
                Welcome to Your Dashboard
              </h2>
              <div className="w-8 h-px mx-auto mb-6" style={{ backgroundColor: CHARCOAL }} />
              <p className="font-poppinsLight text-gray-500 text-sm sm:text-base max-w-2xl mx-auto px-1 leading-relaxed">
                Monitor your performance, manage your profile, and track your earnings all in one place
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-8 xl:gap-10">
              <div className="min-w-0 space-y-8 order-2 lg:order-1">
            {profile?.applicationStatus === 'approved' ? (
              <div className="space-y-8">
                {/* Hotel Booking Performance */}
                <div className="mb-10 sm:mb-12">
                  <DashboardSectionHeader
                    eyebrow="Performance"
                    title="Hotel Booking Performance"
                    rightSlot={(
                      <div className="flex items-center w-fit max-w-full gap-2 text-xs sm:text-sm text-gray-500 bg-white px-3 py-1.5 border border-gray-200/60">
                        <Clock className="w-4 h-4 shrink-0" />
                        <span className="whitespace-nowrap sm:whitespace-normal font-poppinsLight">
                          Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  />

                  {isLoadingStats ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                      {[...Array(4)].map((_, index) => (
                        <div key={index} className="bg-white p-5 sm:p-6 border border-gray-200/60 animate-pulse">
                          <div className="flex items-center space-x-4 mb-4">
                            <div className="w-10 h-10 bg-gray-100" />
                            <div className="space-y-2 flex-1">
                              <div className="h-3 bg-gray-100 w-24" />
                              <div className="h-4 bg-gray-100 w-16" />
                            </div>
                          </div>
                          <div className="h-10 bg-gray-100 w-32 mt-4" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                      <PerformanceStatCard
                        icon={DollarSign}
                        label="Month Earnings"
                        title="Commissions"
                        value={`$${dashboardStats?.currentMonthCommissions?.toFixed(2) || '0.00'}`}
                        footnote={`${dashboardStats?.currentCommissionRate || 5}% Commission Rate`}
                      />
                      <PerformanceStatCard
                        icon={CheckCircle}
                        label="Currently Active"
                        title="Confirmed"
                        value={dashboardStats?.confirmedBookings || 0}
                        subtitle="Active reservations"
                      />
                      <PerformanceStatCard
                        icon={Package}
                        label="Month Volume"
                        title="Bookings"
                        value={dashboardStats?.totalBookings || 0}
                        subtitle="Life-time bookings"
                      />
                      <PerformanceStatCard
                        icon={XCircle}
                        label="Status Update"
                        title="Cancelled"
                        value={(dashboardStats?.cancelledBookings || 0) + (dashboardStats?.refundBookings || 0)}
                        subtitle="Refunds processed"
                      />
                    </div>
                  )}
                </div>

                {/* Experience Performance */}
                <div className="mb-10 sm:mb-12 border-t border-gray-200/60 pt-10 sm:pt-12">
                  <DashboardSectionHeader
                    eyebrow="Performance"
                    title="Experience Performance"
                    rightSlot={(
                      <div className="flex items-center w-fit max-w-full gap-2 text-xs sm:text-sm text-gray-500 bg-white px-3 py-1.5 border border-gray-200/60">
                        <Clock className="w-4 h-4 shrink-0" />
                        <span className="whitespace-nowrap sm:whitespace-normal font-poppinsLight">
                          Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  />

                  {isLoadingStats ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                      {[...Array(4)].map((_, index) => (
                        <div key={index} className="bg-white p-5 sm:p-6 border border-gray-200/60 animate-pulse">
                          <div className="flex items-center space-x-4 mb-4">
                            <div className="w-10 h-10 bg-gray-100" />
                            <div className="space-y-2 flex-1">
                              <div className="h-3 bg-gray-100 w-24" />
                              <div className="h-4 bg-gray-100 w-16" />
                            </div>
                          </div>
                          <div className="h-10 bg-gray-100 w-32 mt-4" />
                        </div>
                      ))}
                    </div>
                  ) : dashboardStats?.experienceStats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                      <PerformanceStatCard
                        icon={DollarSign}
                        label="Month Earnings"
                        title="Commissions"
                        value={`$${dashboardStats.experienceStats.currentMonthCommission?.totalCommissionForMonth?.toFixed(2) || '0.00'}`}
                        footnote={`${dashboardStats.experienceStats.currentMonthCommission?.allocatedCommissionRate || 9}% Commission Rate`}
                      />
                      <PerformanceStatCard
                        icon={CheckCircle}
                        label="Currently Active"
                        title="Confirmed"
                        value={dashboardStats.experienceStats.currentMonth?.confirmed || 0}
                        subtitle="Active experiences"
                      />
                      <PerformanceStatCard
                        icon={Package}
                        label="Month Volume"
                        title="Bookings"
                        value={dashboardStats.experienceStats.currentMonth?.total || 0}
                        subtitle="Monthly total"
                      />
                      <PerformanceStatCard
                        icon={XCircle}
                        label="Status Update"
                        title="Cancelled"
                        value={dashboardStats.experienceStats.currentMonth?.cancelled || 0}
                        subtitle="Refunds processed"
                      />
                    </div>
                  )}
                </div>


                {/* Luxury Quick Actions & Status */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                  {/* Quick Actions Panel */}
                  <div className="lg:col-span-2 bg-white p-4 sm:p-8 border border-gray-200/60">
                    <div className="flex items-center space-x-4 mb-8">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                        <Settings className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Quick Actions</h3>
                        <p className="text-gray-500 font-poppinsLight text-sm">Manage your business efficiently</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        onClick={() => navigate('/experiences')}
                        className="group relative overflow-hidden p-6 bg-white border border-gray-200/60 hover:border-gray-900 transition-colors duration-200 text-left"
                      >
                        <div className="relative z-10">
                          <div className="w-10 h-10 flex items-center justify-center mb-4 border border-gray-200/60 group-hover:border-gray-900 transition-colors duration-200">
                            <Globe className="h-5 w-5 text-gray-800 group-hover:text-gray-900 transition-colors" />
                          </div>
                          <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5 group-hover:translate-x-1 transition-transform" style={{ color: DARK }}>Browse Experiences</h4>
                          <p className="text-sm font-poppinsLight text-gray-500">Curated activities</p>
                        </div>
                      </button>

                      <button
                        onClick={() => navigate('/hotels')}
                        className="group relative overflow-hidden p-6 bg-white border border-gray-200/60 hover:border-gray-900 transition-colors duration-200 text-left"
                      >
                        <div className="relative z-10">
                          <div className="w-10 h-10 bg-white flex items-center justify-center mb-4 border border-gray-200/60 group-hover:border-gray-900 transition-colors duration-200">
                            <Hotel className="h-5 w-5 text-gray-800 group-hover:text-gray-900 transition-colors" />
                          </div>
                          <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5 group-hover:translate-x-1 transition-transform" style={{ color: DARK }}>Browse Hotels</h4>
                          <p className="text-sm font-poppinsLight text-gray-500">Access luxury inventory</p>
                        </div>
                      </button>

                      <button
                        onClick={() => navigate('/private-jets')}
                        className="group relative overflow-hidden p-6 bg-white border border-gray-200/60 hover:border-gray-900 transition-colors duration-200 text-left"
                      >
                        <div className="relative z-10">
                          <div className="w-10 h-10 bg-white flex items-center justify-center mb-4 border border-gray-200/60 group-hover:border-gray-900 transition-colors duration-200">
                            <Plane className="h-5 w-5 text-gray-800 group-hover:text-gray-900 transition-colors" />
                          </div>
                          <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5 group-hover:translate-x-1 transition-transform" style={{ color: DARK }}>Private Jets</h4>
                          <p className="text-sm font-poppinsLight text-gray-500">Charter and fleet access</p>
                        </div>
                      </button>

                      <button
                        onClick={() => navigate('/profile')}
                        className="group relative overflow-hidden p-6 bg-white border border-gray-200/60 hover:border-gray-900 transition-colors duration-200 text-left"
                      >
                        <div className="relative z-10">
                          <div className="w-10 h-10 bg-white flex items-center justify-center mb-4 border border-gray-200/60 group-hover:border-gray-900 transition-colors duration-200">
                            <User className="h-5 w-5 text-gray-800 group-hover:text-gray-900 transition-colors" />
                          </div>
                          <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5 group-hover:translate-x-1 transition-transform" style={{ color: DARK }}>Manage Profile</h4>
                          <p className="text-sm font-poppinsLight text-gray-500">Update business details</p>
                        </div>
                      </button>

                      <button
                        onClick={() => navigate('/bank-account')}
                        className="group relative overflow-hidden p-6 bg-white border border-gray-200/60 hover:border-gray-900 transition-colors duration-200 text-left"
                      >
                        <div className="relative z-10">
                          <div className="w-10 h-10 bg-white flex items-center justify-center mb-4 border border-gray-200/60 group-hover:border-gray-900 transition-colors duration-200">
                            <CreditCard className="h-5 w-5 text-gray-800 group-hover:text-gray-900 transition-colors" />
                          </div>
                          <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5 group-hover:translate-x-1 transition-transform" style={{ color: DARK }}>Payment Methods</h4>
                          <p className="text-sm font-poppinsLight text-gray-500">Manage bank account</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Account Status Panel */}
                  <div className="p-4 sm:p-8 text-white relative overflow-hidden border border-gray-800" style={{ backgroundColor: DARK }}>
                    <div className="relative z-10 h-full flex flex-col">
                      <div className="flex items-center space-x-3 mb-8">
                        <div className="w-12 h-12 flex items-center justify-center border border-white/10" style={{ backgroundColor: CHARCOAL }}>
                          <CheckCircle className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-AXONALLPrimeRegular tracking-wider uppercase text-white">Account Status</h3>
                          <p className="text-white/50 font-poppinsLight text-sm">Verification level</p>
                        </div>
                      </div>

                      <div className="space-y-4 flex-grow">
                        <div className="flex items-center justify-between p-4 border border-white/10" style={{ backgroundColor: 'rgba(58,58,58,0.35)' }}>
                          <span className="text-sm font-poppinsLight text-white/50">Application</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                            <span className="text-sm font-poppinsRegular font-semibold text-white">Approved</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-4 border border-white/10" style={{ backgroundColor: 'rgba(58,58,58,0.35)' }}>
                          <span className="text-sm font-poppinsLight text-white/50">Email Status</span>
                          <div className="flex items-center space-x-2">
                            {profile?.emailVerified ? (
                              <>
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                                <span className="text-sm font-poppinsRegular font-semibold text-white">Verified</span>
                              </>
                            ) : (
                              <>
                                  <div className="w-2 h-2 rounded-full bg-neutral-500"></div>
                                  <span className="text-sm font-poppinsRegular font-semibold text-neutral-300">Pending</span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-4 border border-white/10" style={{ backgroundColor: 'rgba(58,58,58,0.35)' }}>
                          <span className="text-sm font-poppinsLight text-white/50">Business Type</span>
                          <span className="text-sm font-poppinsRegular font-semibold text-white">{profile?.businessType || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t border-white/10 text-center">
                        <p className="text-xs font-poppinsLight text-white/40">Member since {new Date(profile?.createdAt || Date.now()).getFullYear()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upcoming Bookings */}
                {/* {dashboardStats?.upcomingBookings && dashboardStats.upcomingBookings.length > 0 && (
                <div className="bg-gradient-to-br from-white to-slate-50 border border-neutral-200 rounded-2xl p-4 sm:p-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-AXONALLPrimeRegular font-bold text-neutral-900">Upcoming Bookings</h3>
                        <p className="text-neutral-600">Active reservations</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/user-bookings')}
                      className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200"
                    >
                      View All
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {dashboardStats.upcomingBookings.slice(0, 4).map((booking, index) => (
                      <div key={index} className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200 hover:shadow-md transition-all duration-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Booking ID</p>
                            <p className="text-lg font-mono font-bold text-neutral-900">{booking.bookingId}</p>
                          </div>
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                            {booking.status}
                          </span>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Destination</p>
                            <p className="text-sm font-semibold text-neutral-900">{booking.destination}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Check-in</p>
                              <p className="text-sm font-semibold text-neutral-900">{new Date(booking.checkIn).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Check-out</p>
                              <p className="text-sm font-semibold text-neutral-900">{new Date(booking.checkOut).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="pt-3 border-t border-neutral-200">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Commission</span>
                              <span className="text-lg font-bold text-green-600">${booking.commission.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )} */}


                {/* Carbon Emission Stats */}
                {dashboardStats?.carbonEmission && (
                  <div className="space-y-6">
                    <div className="bg-white border border-gray-200/60 p-4 sm:p-6">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                            <Leaf className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Environmental Impact</h3>
                            <p className="text-sm font-poppinsLight text-gray-500">Carbon footprint for {dashboardStats.carbonEmission.year}</p>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-2 mb-2">
                            <Leaf className="h-4 w-4" style={{ color: CHARCOAL }} />
                            <p className="text-sm font-poppinsLight text-gray-500">Carbon Emitted</p>
                          </div>
                          <p className="text-3xl font-poppinsRegular text-neutral-900">{dashboardStats.carbonEmission.totalEmitted.toFixed(2)}</p>
                          <p className="text-xs font-poppinsLight text-gray-500 mt-1">kg CO₂</p>
                        </div>
                        <div className="bg-white p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-2 mb-2">
                            <Zap className="h-4 w-4" style={{ color: CHARCOAL }} />
                            <p className="text-sm font-poppinsLight text-gray-500">Electricity Used</p>
                          </div>
                          <p className="text-3xl font-poppinsRegular text-neutral-900">{dashboardStats.carbonEmission.totalElectricity.toFixed(2)}</p>
                          <p className="text-xs font-poppinsLight text-gray-500 mt-1">kWh</p>
                        </div>
                        <div className="bg-white p-4 border border-gray-200/60">
                          <div className="flex items-center space-x-2 mb-2">
                            <TrendingUp className="h-4 w-4" style={{ color: CHARCOAL }} />
                            <p className="text-sm font-poppinsLight text-gray-500">Emission Records</p>
                          </div>
                          <p className="text-3xl font-poppinsRegular text-neutral-900">{dashboardStats.carbonEmission.recordsCount}</p>
                          <p className="text-xs font-poppinsLight text-gray-500 mt-1">Total records</p>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200/60">
                        <p className="text-xs font-poppinsLight text-gray-500 text-center">
                          Tracking your environmental impact to promote sustainable travel practices
                        </p>
                      </div>
                    </div>

                    {/* Client Environmental Impact Report Section */}
                    {/* <div className="bg-gradient-to-br from-white to-slate-50 border border-neutral-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-AXONALLPrimeRegular font-semibold text-neutral-900">Client Environmental Impact Report</h3>
                          <p className="text-sm font-poppinsRegular text-neutral-500">Generate detailed environmental reports for clients</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <div className="relative">
                        <label className="block text-sm font-poppinsRegular font-medium text-neutral-700 mb-2">
                          Select Client
                        </label>
                        {isLoadingClients ? (
                          <div className="animate-pulse">
                            <div className="w-full h-10 bg-neutral-200 rounded-lg"></div>
                          </div>
                        ) : (
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                            <input
                              type="text"
                              placeholder={selectedClient ? "Client selected" : "Search clients..."}
                              value={selectedClient ?
                                clients.find(c => c.customerId === selectedClient)?.fullName ||
                                `${clients.find(c => c.customerId === selectedClient)?.firstName || ''} ${clients.find(c => c.customerId === selectedClient)?.lastName || ''}`.trim()
                                : searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                              }}
                              onFocus={() => {
                                setIsInputFocused(true);
                                if (selectedClient) {
                                  setSelectedClient('');
                                  setSearchTerm('');
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => setIsInputFocused(false), 200);
                              }}
                              className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            {selectedClient && (
                              <button
                                onClick={() => {
                                  setSelectedClient('');
                                  setSearchTerm('');
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}

                        {(isInputFocused || searchTerm) && !selectedClient && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <button
                                  key={client.customerId}
                                  className="w-full px-4 py-2 text-left hover:bg-neutral-50 border-b border-neutral-200 last:border-b-0"
                                  onClick={() => {
                                    setSelectedClient(client.customerId);
                                    setSearchTerm('');
                                    setIsInputFocused(false);
                                  }}
                                >
                                  <div className="font-medium text-neutral-900">
                                    {client.fullName || `${client.firstName} ${client.lastName}`}
                                  </div>
                                  <div className="text-sm text-neutral-500">{client.email}</div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-neutral-500">
                                {searchTerm ? 'No clients found' : 'No clients available'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-poppinsRegular font-medium text-neutral-700 mb-2">
                          Start Date
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-poppinsRegular font-medium text-neutral-700 mb-2">
                          End Date
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={handleSearchClient}
                          disabled={isLoadingReport || !selectedClient || !startDate || !endDate}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                        >
                          {isLoadingReport ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                          <span className="font-poppinsRegular">{isLoadingReport ? 'Generating...' : 'Generate Report'}</span>
                        </button>
                      </div>
                    </div>

                    {isLoadingReport ? (
                      <div className="bg-white rounded-xl border border-neutral-200 p-6">
                        <div className="flex items-center justify-center py-12">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                            <p className="text-neutral-600 font-poppinsRegular">Generating environmental report...</p>
                          </div>
                        </div>
                      </div>
                    ) : environmentalReport && (
                        <div className="bg-white rounded-xl border border-neutral-200 p-6">
                          <div className="flex items-center justify-between mb-6">
                            <div>
                              <h4 className="text-lg font-AXONALLPrimeRegular font-semibold text-neutral-900">
                                Report for {environmentalReport.client ?
                                  (environmentalReport.client.fullName || `${environmentalReport.client.firstName || ''} ${environmentalReport.client.lastName || ''}`.trim() || 'Unknown Client')
                                  : 'Unknown Client'}
                              </h4>
                              <p className="text-neutral-600 font-poppinsRegular">
                                {environmentalReport.environmentalImpact?.dateRange?.startDate || startDate} to{' '}
                                {environmentalReport.environmentalImpact?.dateRange?.endDate || endDate}
                              </p>
                            </div>
                            <button
                            onClick={handleDownloadPDF}
                            disabled={isGeneratingPDF || !environmentalReport?.environmentalImpact?.bookings?.length}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                          >
                            {isGeneratingPDF ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                              <span className="font-poppinsRegular">{isGeneratingPDF ? 'Generating...' : 'Download PDF'}</span>
                          </button>
                          </div>

                          {!environmentalReport.client && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                              <p className="text-red-800 font-poppinsRegular">Client data is missing from the report.</p>
                            </div>
                          )}

                          {!environmentalReport.environmentalImpact && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                              <p className="text-yellow-800 font-poppinsRegular">Environmental impact data is missing from the report.</p>
                            </div>
                          )}

                          {environmentalReport.environmentalImpact && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-slate-50 rounded-lg p-4 text-center">
                                  <div className="text-2xl font-poppinsRegular font-bold text-emerald-700">
                                    {environmentalReport.environmentalImpact.totalCarbonEmission || 0}
                                  </div>
                                  <div className="text-sm font-poppinsRegular text-neutral-600">kg CO₂ Emitted</div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4 text-center">
                                  <div className="text-2xl font-poppinsRegular font-bold text-amber-700">
                                    {environmentalReport.environmentalImpact.totalElectricityValue || 0}
                                  </div>
                                  <div className="text-sm font-poppinsRegular text-neutral-600">kWh Used</div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-4 text-center">
                                  <div className="text-2xl font-poppinsRegular font-bold text-blue-700">
                                    {environmentalReport.environmentalImpact.bookingCount || 0}
                                  </div>
                                  <div className="text-sm font-poppinsRegular text-neutral-600">Bookings</div>
                                </div>
                              </div>

                              {environmentalReport.environmentalImpact.bookings && environmentalReport.environmentalImpact.bookings.length > 0 ? (
                                <div className="space-y-4">
                                  <h5 className="font-AXONALLPrimeRegular font-semibold text-neutral-900">Bookings Details</h5>
                                  {environmentalReport.environmentalImpact.bookings.map((booking, index) => (
                                    <div key={index} className="border border-neutral-200 rounded-lg p-4">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                        <h6 className="font-AXONALLPrimeRegular font-semibold text-neutral-900">{booking.hotelName || 'Unknown Hotel'}</h6>
                                        <p className="text-sm font-poppinsRegular text-neutral-600">Booking ID: {booking.confirmationId || 'N/A'}</p>
                                      </div>
                                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-poppinsRegular rounded-full">
                                        {booking.bookingStatus || 'Unknown'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-poppinsRegular">
                                      <div>
                                        <span className="font-medium">Carbon Emission:</span>{' '}
                                        {booking.environmentalImpact?.carbonEmission || 0} kg
                                      </div>
                                      <div>
                                        <span className="font-medium">Electricity:</span>{' '}
                                        {booking.environmentalImpact?.electricityValue || 0} kWh
                                      </div>
                                      <div>
                                        <span className="font-medium">Tier:</span> {booking.environmentalImpact?.tier || 'No data'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                </div>
                              ) : (
                              <div className="text-center py-8 text-neutral-500 font-poppinsRegular">
                                No booking data found for the selected criteria
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div> */}
                  </div>
                )}


                {/* Recent Bookings */}
                {isLoadingStats ? (
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                    <div className="animate-pulse">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-200"></div>
                          <div className="space-y-2">
                            <div className="h-6 bg-neutral-200 rounded w-48"></div>
                            <div className="h-4 bg-neutral-200 rounded w-32"></div>
                          </div>
                        </div>
                        <div className="h-8 bg-neutral-200 rounded w-24"></div>
                      </div>
                      <div className="space-y-4">
                        {[...Array(3)].map((_, index) => (
                          <div key={index} className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
                            <div className="space-y-4">
                              <div className="h-4 bg-neutral-200 rounded w-32"></div>
                              <div className="h-6 bg-neutral-200 rounded w-48"></div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="h-4 bg-neutral-200 rounded"></div>
                                <div className="h-4 bg-neutral-200 rounded"></div>
                                <div className="h-4 bg-neutral-200 rounded"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : dashboardStats?.recentBookings && dashboardStats.recentBookings.length > 0 && (
                    <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ backgroundColor: DARK }}>
                          <Package className="h-6 w-6 text-white" />
                        </div>
                          <div className="min-w-0">
                            <h3 className="text-xl sm:text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Recent Bookings</h3>
                            <p className="text-gray-500 font-poppinsLight text-sm sm:text-base">Latest reservations activity</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/user-bookings')}
                          className="w-full sm:w-auto px-4 py-2 text-[11px] tracking-[0.18em] uppercase font-poppinsRegular text-white hover:bg-black transition-colors duration-200"
                          style={{ backgroundColor: DARK }}
                      >
                        View All
                      </button>
                    </div>
                    <div className="space-y-4">
                      {dashboardStats.recentBookings.slice(0, 5).map((booking, index) => (
                        <div key={index} className="bg-white p-4 sm:p-6 border border-gray-200/60 hover:border-gray-300 transition-all duration-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Booking ID</p>
                                  <p className="text-lg font-mono font-bold text-neutral-900">{booking.bookingId}</p>
                                </div>
                                <span className="px-3 py-1 text-xs font-poppinsRegular uppercase tracking-wide bg-white text-neutral-900 border border-gray-200/60">
                                  {booking.status}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Destination</p>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">{booking.destination}</p>
                                  {renderDashboardStaySourceLine(booking)}
                                </div>
                                <div>
                                  <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Travel Dates</p>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">
                                    {new Date(booking.checkIn).toLocaleDateString()} - {new Date(booking.checkOut).toLocaleDateString()}
                                  </p>
                                </div>
                                {/* <div>
                                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Commission</p>
                                <p className="text-lg font-bold text-green-600">${booking.commission.toFixed(2)}</p>
                              </div> */}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cancellations */}
                {dashboardStats?.recentBookings && dashboardStats.recentBookings.filter(b =>
                  b.status?.toLowerCase() === 'cancelled' || b.status?.toLowerCase() === 'refund' || b.status?.toLowerCase() === 'refunded'
                ).length > 0 && (
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ backgroundColor: DARK }}>
                            <XCircle className="h-6 w-6 text-white" />
                          </div>
                        <div className="min-w-0">
                          <h3 className="text-xl sm:text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Cancellations</h3>
                          <p className="text-gray-500 font-poppinsLight text-sm sm:text-base">Cancelled and refunded bookings</p>
                          </div>
                        </div>
                      <div className="px-4 py-2 bg-white border border-gray-200/60 w-fit">
                        <span className="font-AXONALLPrimeRegular text-lg" style={{ color: DARK }}>
                            {(dashboardStats?.cancelledBookings || 0) + (dashboardStats?.refundBookings || 0)} Total
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {dashboardStats.recentBookings
                          .filter(b => b.status?.toLowerCase() === 'cancelled' || b.status?.toLowerCase() === 'refund' || b.status?.toLowerCase() === 'refunded')
                          .slice(0, 5)
                          .map((booking, index) => (
                            <div key={index} className="bg-white p-4 sm:p-6 border border-gray-200/60 hover:border-gray-400 transition-colors duration-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-start justify-between mb-3">
                                    <div>
                                      <p className="text-sm font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Booking ID</p>
                                      <p className="text-lg font-mono font-bold text-neutral-900">{booking.bookingId}</p>
                                    </div>
                                    <span className="px-3 py-1 text-xs font-poppinsRegular uppercase tracking-wide bg-white text-neutral-900 border border-gray-200/60">
                                      {booking.status}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                      <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Destination</p>
                                      <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">{booking.destination}</p>
                                      {renderDashboardStaySourceLine(booking)}
                                    </div>
                                    <div>
                                      <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Travel Dates</p>
                                      <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">
                                        {new Date(booking.checkIn).toLocaleDateString()} - {new Date(booking.checkOut).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Lost Commission</p>
                                      <p className="text-lg font-poppinsRegular font-bold text-neutral-900">-${(booking.commission || 0).toFixed(2)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Recent Experience Bookings */}
                {dashboardStats?.experienceStats?.recentBookings && dashboardStats.experienceStats.recentBookings.length > 0 && (
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ backgroundColor: DARK }}>
                          <Globe className="h-6 w-6 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-xl sm:text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Recent Experience Bookings</h3>
                          <p className="text-gray-500 font-poppinsLight text-sm sm:text-base">Latest experience reservations</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {dashboardStats.experienceStats.recentBookings.slice(0, 5).map((booking, index) => (
                        <div key={index} className="bg-white p-4 sm:p-6 border border-gray-200/60 hover:border-gray-300 transition-all duration-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Booking Reference</p>
                                  <p className="text-lg font-mono font-bold text-neutral-900">{booking.bookingReference}</p>
                                </div>
                                <span className="px-3 py-1 text-xs font-poppinsRegular uppercase tracking-wide bg-white text-neutral-900 border border-gray-200/60">
                                  {booking.bookingStatus}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Experience</p>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">{booking.experienceTitle}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Date</p>
                                  <p className="text-sm font-poppinsRegular font-semibold text-neutral-900">
                                    {new Date(booking.selectedDate).toLocaleDateString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide mb-1">Total Price</p>
                                  <p className="text-lg font-poppinsRegular font-bold text-neutral-900">{booking.currency} {booking.totalPrice?.toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="mt-3 pt-3 border-t border-gray-200/60">
                                <div className="flex items-center space-x-2">
                                  <User className="h-4 w-4 text-neutral-400" />
                                  <span className="text-sm font-poppinsRegular text-neutral-600">
                                    Guest: <span className="font-poppinsRegular font-medium text-neutral-900">
                                      {booking.guestInfo?.firstName} {booking.guestInfo?.lastName}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics Chart */}
                {/* <AnalyticsChart dashboardStats={dashboardStats} profile={profile} /> */}

                {/* Profile Information */}
                <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                  <div className="flex items-center space-x-3 mb-8">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Profile Information</h3>
                      <p className="text-gray-500 font-poppinsLight">Your business and personal details</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {profile?.name && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <User className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Name</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.name}</p>
                      </div>
                    )}
                    {profile?.email && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Mail className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Email</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.email}</p>
                      </div>
                    )}
                    {profile?.legalBusinessName && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Building className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Business</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.legalBusinessName}</p>
                      </div>
                    )}
                    {profile?.phoneNumber && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Phone className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Phone</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.phoneNumber}</p>
                      </div>
                    )}
                    {profile?.country && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <MapPin className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Country</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.country}</p>
                      </div>
                    )}
                    {profile?.businessType && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Building className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Business Type</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.businessType}</p>
                      </div>
                    )}
                    {profile?.businessPhoneNumber && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Phone className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Business Phone</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.businessPhoneNumber}</p>
                      </div>
                    )}
                    {profile?.businessEmail && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Mail className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Business Email</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.businessEmail}</p>
                      </div>
                    )}
                    {profile?.businessHours && (
                      <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Business Hours</label>
                        </div>
                        <p className="text-neutral-900 font-poppinsRegular font-semibold">{profile.businessHours}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Specializations */}
                {profile?.specializations && profile.specializations.length > 0 && (
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                        <Award className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Specializations</h3>
                        <p className="text-gray-500 font-poppinsLight">Your areas of expertise</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {profile.specializations.map((spec, index) => (
                        <span key={index} className="inline-flex items-center px-4 py-2 bg-white text-neutral-900 text-sm font-poppinsRegular border border-gray-200/60">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {/* Application Status */}
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                  <div className="flex items-center space-x-4 mb-6">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Application Under Review</h3>
                        <p className="text-gray-500 font-poppinsLight">We're carefully reviewing your application</p>
                    </div>
                  </div>
                    <div className="bg-white p-6 border border-gray-200/60">
                      <p className="text-gray-600 font-poppinsLight leading-relaxed text-sm">
                      Your application is being carefully reviewed by our team. You'll receive an email notification once the review is complete.
                      This process typically takes 2-3 business days.
                    </p>
                  </div>
                </div>

                {/* Application Details */}
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                  <div className="flex items-center space-x-3 mb-8">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>Application Details</h3>
                        <p className="text-gray-500 font-poppinsLight">Your submission information</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {profile?.applicationId && (
                        <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                            <Award className="h-4 w-4 text-neutral-500" />
                            <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Application ID</label>
                        </div>
                          <p className="text-neutral-900 font-mono font-semibold">{profile.applicationId}</p>
                      </div>
                    )}
                    {profile?.createdAt && (
                        <div className="bg-white p-4 border border-gray-200/60">
                        <div className="flex items-center space-x-2 mb-2">
                            <Calendar className="h-4 w-4 text-neutral-500" />
                            <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Submitted</label>
                        </div>
                          <p className="text-neutral-900 font-poppinsRegular font-semibold">{new Date(profile.createdAt).toLocaleDateString()}</p>
                      </div>
                    )}
                      <div className="bg-white p-4 border border-gray-200/60">
                      <div className="flex items-center space-x-2 mb-2">
                          <Mail className="h-4 w-4 text-neutral-500" />
                          <label className="text-xs font-poppinsRegular font-semibold text-neutral-500 uppercase tracking-wide">Email Verified</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        {profile?.emailVerified ? (
                          <>
                              <CheckCircle className="h-4 w-4 text-neutral-800" />
                              <span className="text-neutral-900 font-poppinsRegular font-semibold">Verified</span>
                          </>
                        ) : (
                          <>
                                <AlertCircle className="h-4 w-4 text-neutral-500" />
                                <span className="text-neutral-900 font-poppinsRegular font-semibold">Not Verified</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                  <div className="bg-white border border-gray-200/60 p-4 sm:p-8">
                  <div className="flex items-center space-x-3 mb-8">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: DARK }}>
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-2xl font-AXONALLPrimeRegular tracking-wider uppercase" style={{ color: DARK }}>What Happens Next?</h3>
                        <p className="text-gray-500 font-poppinsLight">Your journey to becoming a partner</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white p-6 border border-gray-200/60">
                      <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: DARK }}>
                          <span className="text-white font-bold text-lg">1</span>
                        </div>
                        <div>
                            <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-2" style={{ color: DARK }}>Review Process</h4>
                            <p className="text-gray-500 font-poppinsLight text-sm leading-relaxed">Our team will review your application and business credentials thoroughly.</p>
                        </div>
                      </div>
                    </div>
                      <div className="bg-white p-6 border border-gray-200/60">
                      <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: DARK }}>
                          <span className="text-white font-bold text-lg">2</span>
                        </div>
                        <div>
                            <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-2" style={{ color: DARK }}>Email Notification</h4>
                            <p className="text-gray-500 font-poppinsLight text-sm leading-relaxed">You'll receive an email with the review results and next steps.</p>
                        </div>
                      </div>
                    </div>
                      <div className="bg-white p-6 border border-gray-200/60">
                      <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: DARK }}>
                          <span className="text-white font-bold text-lg">3</span>
                        </div>
                        <div>
                            <h4 className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-2" style={{ color: DARK }}>Access Granted</h4>
                            <p className="text-gray-500 font-poppinsLight text-sm leading-relaxed">Once approved, you'll have full access to our luxury travel platform.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

              </div>

              {/* Sidebar — desktop only (inline) */}
              <div className="hidden lg:block lg:order-2 lg:max-w-none">
                <StudioInventorySidebar
                  slides={studioCarouselSlides}
                  isLoading={isLoadingFeatured}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: floating trigger tab ─────────────────────── */}
      <button
        type="button"
        onClick={() => setStudioDrawerOpen(true)}
        className="lg:hidden fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center gap-1.5 py-5 px-2.5 text-white"
        style={{ backgroundColor: DARK, writingMode: 'vertical-rl' }}
        aria-label="Open From the Studio"
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ writingMode: 'horizontal-tb' }} />
        <span className="text-[9px] tracking-[0.25em] uppercase font-poppinsRegular" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          From the Studio
        </span>
      </button>

      {/* ── Mobile: slide-in drawer ───────────────────────────── */}
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${studioDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setStudioDrawerOpen(false)}
        aria-hidden
      />
      {/* Panel */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full z-50 w-[85vw] max-w-sm bg-white overflow-y-auto transition-transform duration-300 ease-in-out ${studioDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ boxShadow: '-4px 0 32px rgba(0,0,0,0.18)' }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight" style={{ color: CHARCOAL }}>
            From the Studio
          </p>
          <button
            type="button"
            onClick={() => setStudioDrawerOpen(false)}
            className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors"
            aria-label="Close"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        {/* Sidebar content inside drawer */}
        <div className="p-4">
          <StudioInventorySidebar
            slides={studioCarouselSlides}
            isLoading={isLoadingFeatured}
            hideHeader
          />
        </div>
      </div>

    </MainLayout>
  )
}

export default DashboardPage